import { apiMerchantPublic } from "@/api";
import { getOrCreateSessionId } from "@/domain/storefront-analytics";

import { parseDaypartResponse, parseUpsellResponse } from "./parsers";
import { DAYPART_DISPLAY_CAP, UPSELL_DISPLAY_CAP } from "./types";
import type { DaypartResponse, UpsellResponse, UpsellSurface } from "./types";

const REQUEST_TIMEOUT_MS = 1500;

/** Empty, "keep asking" answers — the safe default when a call never completes. */
const EMPTY_UPSELL: UpsellResponse = { enabled: true, suggestions: [], exploreSlots: 0 };
/** Same fallback shape `parseDaypartResponse` uses for a body with no usable band. */
const EMPTY_DAYPART: DaypartResponse = parseDaypartResponse({ enabled: true, suggestions: [] });

/**
 * WHERE THE STOREFRONT READS SUGGESTIONS
 *
 * Read-only, public, unauthenticated routes. Never rejects: a suggestion rail
 * is a showcase, never a checkout blocker — network/timeout/corrupt payload
 * all become an empty (but still `enabled: true`) answer, exactly like
 * `BestsellersSource`. Callers get `enabled: false` ONLY when the merchant
 * really turned the recommender/surface off.
 */
export interface SuggestionsSource {
  getUpsell(
    merchantId: string,
    anchorItemIds: string[],
    excludeItemIds: string[],
    surface: UpsellSurface,
    slots?: number,
  ): Promise<UpsellResponse>;
  getDaypart(merchantId: string, excludeItemIds: string[], slots?: number): Promise<DaypartResponse>;
}

function sortedKey(ids: string[]): string {
  return [...ids].sort().join(",");
}

function buildQuery(anchorOrExclude: string[]): string {
  return anchorOrExclude.map(encodeURIComponent).join(",");
}

/**
 * Public `GET /public/merchants/:id/suggestions/{upsell,daypart}`.
 *
 * - Hard 1.5s client timeout on top of whatever the gateway's own 150ms
 *   serving budget already caps — a slow network must not delay the cart.
 * - Per-session memo keyed by merchant + surface + sorted anchors + sorted
 *   excludes: the same cart renders the strip more than once (impression
 *   observer remounts, cart re-renders) without re-asking the network.
 * - Per-surface "the store turned this off" latch: once a call answers
 *   `enabled: false` for a (merchant, surface) pair, every later call for
 *   that pair in this session short-circuits to `{ enabled: false }` without
 *   hitting the network again.
 */
export class HttpSuggestionsSource implements SuggestionsSource {
  private readonly cache = new Map<string, Promise<UpsellResponse | DaypartResponse>>();
  private readonly disabledSurfaces = new Set<string>();

  async getUpsell(
    merchantId: string,
    anchorItemIds: string[],
    excludeItemIds: string[],
    surface: UpsellSurface,
    slots: number = UPSELL_DISPLAY_CAP,
  ): Promise<UpsellResponse> {
    const disableKey = `${merchantId}:${surface}`;
    if (this.disabledSurfaces.has(disableKey)) return { enabled: false };

    const cacheKey = `upsell:${merchantId}:${surface}:${slots}:${sortedKey(anchorItemIds)}:${sortedKey(excludeItemIds)}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached as Promise<UpsellResponse>;

    const promise = (async (): Promise<UpsellResponse> => {
      try {
        const params = new URLSearchParams();
        params.set("items", buildQuery(anchorItemIds));
        if (excludeItemIds.length > 0) params.set("exclude", buildQuery(excludeItemIds));
        params.set("surface", surface.toLowerCase());
        params.set("session", getOrCreateSessionId(merchantId));
        params.set("slots", String(slots));

        const { data } = await apiMerchantPublic.get<unknown>(
          `/public/merchants/${encodeURIComponent(merchantId)}/suggestions/upsell?${params.toString()}`,
          { timeout: REQUEST_TIMEOUT_MS },
        );
        const parsed = parseUpsellResponse(data);
        if (!parsed.enabled) this.disabledSurfaces.add(disableKey);
        return parsed;
      } catch {
        return EMPTY_UPSELL;
      }
    })();

    this.cache.set(cacheKey, promise);
    return promise;
  }

  async getDaypart(
    merchantId: string,
    excludeItemIds: string[],
    slots: number = DAYPART_DISPLAY_CAP,
  ): Promise<DaypartResponse> {
    const disableKey = `${merchantId}:DAYPART`;
    if (this.disabledSurfaces.has(disableKey)) return { enabled: false };

    const cacheKey = `daypart:${merchantId}:${slots}:${sortedKey(excludeItemIds)}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached as Promise<DaypartResponse>;

    const promise = (async (): Promise<DaypartResponse> => {
      try {
        const params = new URLSearchParams();
        if (excludeItemIds.length > 0) params.set("exclude", buildQuery(excludeItemIds));
        params.set("session", getOrCreateSessionId(merchantId));
        params.set("slots", String(slots));

        const { data } = await apiMerchantPublic.get<unknown>(
          `/public/merchants/${encodeURIComponent(merchantId)}/suggestions/daypart?${params.toString()}`,
          { timeout: REQUEST_TIMEOUT_MS },
        );
        const parsed = parseDaypartResponse(data);
        if (!parsed.enabled) this.disabledSurfaces.add(disableKey);
        return parsed;
      } catch {
        return EMPTY_DAYPART;
      }
    })();

    this.cache.set(cacheKey, promise);
    return promise;
  }

  /** Tests only: forget memoized answers and disabled surfaces. */
  resetSessionState(): void {
    this.cache.clear();
    this.disabledSurfaces.clear();
  }
}

/** Sole composition point — no flag; no alternate source today. */
export const suggestionsSource: SuggestionsSource & { resetSessionState(): void } = new HttpSuggestionsSource();
